<template>
  <div
    class="subscribe-widget"
    :style="widgetStyle"
  >
    <div
      class="subscribe-widget__content"
      :style="contentStyle"
    >
      <div :class="{ 'pointer-events-none': untouchable }">
        <div class="hero-24 lh-32 break-word">
          {{ widgetData.header }}
        </div>

        <div class="body-text-semi-bold-14 lh-20 mt-1 break-word">
          {{ widgetData.description }}
        </div>

        <div
          v-if="completed"
          class="subscribe-widget__success pa-2 mt-2 break-word"
        >
          <span v-safe-html="successMessage" />
        </div>

        <subscribe-widget-form
          v-else
          :widget-data="widgetData"
          @submit="$emit('submit')"
        />

        <subscribe-consent
          class="mt-4"
          field-name="consented"
          :text="widgetData.legalInformation"
          :is-required="widgetData.isUserConsentEnabled && !completed"
        />

        <subscribe-consent
          v-if="widgetData.additionalConsent"
          class="mt-4"
          field-name="additionalConsented"
          :text="widgetData.additionalConsent"
          :is-required="widgetData.isUserAdditionalConsentEnabled && !completed"
        />

        <div
          v-if="widgetData.privacyPolicyUrl || widgetData.smsTermsUrl"
          class="mt-4 d-flex flex-center justify-center"
        >
          <subscribe-widget-urls
            :privacy-policy-url="widgetData.privacyPolicyUrl"
            :sms-terms-url="widgetData.smsTermsUrl"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import SubscribeWidgetForm from '@widgets/subscribe/components/SubscribeWidgetForm.vue'
import SubscribeWidgetUrls from '@widgets/subscribe/components/SubscribeWidgetUrls.vue'
import SubscribeConsent from '@widgets/subscribe/components/SubscribeConsent.vue'
import { computed } from '@/composition/vue/compositionApi'
import type {
  SubscribeColorTheme,
  SubscribeFontFamily,
  SubscribeWidgetData,
} from '@/services/domain/services/forms/subscribe/types'
import { styleVars } from '@/constants/styleVariables'
import { addPxToNumber } from '@/utils/string/addPxToNumber'
import { createContentFromTemplate } from '@/services/forms/tmTiptap/utils/createContentFromTemplate'

const props = defineProps<{
  widgetData: SubscribeWidgetData
  completed?: boolean
  untouchable?: boolean
}>()

defineEmits<{
  submit: []
}>()

const calcFontColor = () => {
  if (props.widgetData.colorTheme === 'custom') return props.widgetData.fontColor
  if (props.widgetData.colorTheme === 'dark') return styleVars.white
  return styleVars.black
}

const backgroundColorMap = computed<Record<SubscribeColorTheme, string | undefined>>(() => ({
  blue: styleVars.blue50,
  dark: styleVars.black,
  light: styleVars.white,
  custom: props.widgetData.backgroundColor,
}))

const successColorMap: Record<SubscribeColorTheme, string> = {
  blue: '#89c7e8',
  dark: '#303030',
  light: '#e3eef1',
  custom: '#e3eef1',
}

const fontFamilyMap: Record<SubscribeFontFamily, string> = {
  arial: 'Arial',
  times: 'Times New Roman',
  verdana: 'Verdana',
}

const getBorder = (theme: SubscribeColorTheme | undefined) => {
  switch (theme) {
    case 'light':
      return '1px solid initial'
    case 'blue':
      return `1px solid ${styleVars.gray2}`
    default:
      return 'unset'
  }
}

const widgetStyle = computed(() => ({
  '--font-color': calcFontColor(),
  '--success-color': props.widgetData.colorTheme && successColorMap[props.widgetData.colorTheme],
  width: addPxToNumber((props.widgetData.width || 0) + 20),
}))

const successMessage = computed(() => {
  return createContentFromTemplate(props.widgetData.successMessage, false)
})

const contentStyle = computed(() => ({
  fontFamily: props.widgetData.fontFamily && fontFamilyMap[props.widgetData.fontFamily],
  backgroundColor: props.widgetData.colorTheme && backgroundColorMap.value[props.widgetData.colorTheme],
  width: addPxToNumber(props.widgetData.width || 0),
  border: getBorder(props.widgetData.colorTheme),
}))
</script>

<style lang="scss" scoped>
.subscribe-widget {
  --font-color: unset;
  --success-color: unset;

  &__content {
    border-radius: $border-radius-md;
    margin: 10px;
    padding: 34px;
    color: var(--font-color);
    box-shadow: rgba(0, 0, 0, 0.04) 0 0 12px;
    width: 100%;
  }

  &__success {
    background: var(--success-color);
    pointer-events: auto;
  }

  // for input labels
  :deep(h6) {
    color: var(--font-color) !important;
  }
}
</style>
